import PropTypes from 'prop-types';
import type { ValidationMap } from 'prop-types';
import React, { Fragment, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import _ from 'underscore';
import { Input } from '@bingads-webui-react/primitive-with-signal';
import { ErrorMessages } from '@bingads-webui-react/validation';
import { LineByLineFormGroup } from '@bingads-webui-cc-react/layout';
import { ValidationErrorContext } from '@bingads-webui-cc-react/validation-error-context';
import type { ValidationError } from '@bingads-webui-cc-react/validation-error-context';
import { getUrlBuilders } from '@bingads-webui-clientcenter/url-builder-base';
import { createAsyncToken } from '@bingads-webui-universal/primitive-utilities';
import { Constants } from '@bingads-webui-clientcenter/entity-utils';
import { CouponLength, GenericErrorKey, i18nPropType, CouponCodeFieldId } from './constants';
import type {
  CouponInputI18n,
  CouponInputProps,
  CouponRestfulService,
  CouponUrlBuilder,
  CouponValidationResponse,
} from './types';

const boldStart = '<strong>';
const boldEnd = '</strong>';

const startCouponValidation = (
  i18n: CouponInputI18n,
  couponCode: string,
  setCouponInfo: Dispatch<SetStateAction<string | null>>,
  setMessages: Dispatch<SetStateAction<string[]>>,
  setLoading: CouponInputProps['setLoading'],
  restfulService: CouponRestfulService,
  couponBaseUrlBuilder: CouponUrlBuilder
): () => void => {
  setLoading(true);
  couponBaseUrlBuilder.params({
    ...couponBaseUrlBuilder.configs.params,
    couponCode,
  });

  const promise = restfulService.get(couponBaseUrlBuilder.url)
    .catch(() => {
      throw new Error(i18n.getString(GenericErrorKey));
    })
    .then(({
      UpfrontSpendCurrency,
      IsAccountTaxable,
      ValueCurrency,
      ValidationErrorMessage,
      PercentOff,
      PurposeId,
    }: CouponValidationResponse) => {
      if (ValidationErrorMessage) {
        throw new Error(ValidationErrorMessage);
      }
      if (!ValueCurrency) {
        throw new Error(i18n.getString(GenericErrorKey));
      }

      // Note: We will be using Dangerously dangerouslySetInnerHTML,
      // so make sure everything except boldStart and boldEnd are escaped

      // PercentOff coupons can show two different strings depending on UpfrontSpendCurrency
      let couponString = '';
      if (PercentOff) {
        if (UpfrontSpendCurrency) {
          couponString = i18n.getString(
            IsAccountTaxable
              ? _TL_('{{boldStart}}Spend {{upfront}}{{boldEnd}}, get {{percent}}% off up to {{value}} (excluding taxes).')
              : _TL_('{{boldStart}}Spend {{upfront}}{{boldEnd}}, get {{percent}}% off up to {{value}}.'),
            {
              percent: _.escape(PercentOff),
              value: _.escape(ValueCurrency),
              upfront: _.escape(UpfrontSpendCurrency),
              boldStart,
              boldEnd,
            },
            {
              preprocess: _.escape,
            }
          );
        } else {
          couponString = i18n.getString(
            IsAccountTaxable
              ? _TL_('{{boldStart}}{{percent}}% off{{boldEnd}} up to {{value}} (excluding taxes).')
              : _TL_('{{boldStart}}{{percent}}% off{{boldEnd}} up to {{value}}.'),
            {
              percent: _.escape(PercentOff),
              value: _.escape(ValueCurrency),
              boldStart,
              boldEnd,
            },
            {
              preprocess: _.escape,
            }
          );
        }
      } else if (UpfrontSpendCurrency) {
        couponString = i18n.getString(
          IsAccountTaxable
            ? _TL_('{{boldStart}}Get {{value}}{{boldEnd}} when you spend {{upfront}} (excluding taxes).')
            : _TL_('{{boldStart}}Get {{value}}{{boldEnd}} when you spend {{upfront}}.'),
          {
            value: _.escape(ValueCurrency),
            upfront: _.escape(UpfrontSpendCurrency),
            boldStart,
            boldEnd,
          },
          {
            preprocess: _.escape,
          }
        );
      } else {
        couponString = i18n.getString(
          _TL_('{{boldStart}}{{value}}{{boldEnd}} off.'),
          {
            value: _.escape(ValueCurrency),
            boldStart,
            boldEnd,
          },
          {
            preprocess: _.escape,
          }
        );
      }

      if (parseInt(PurposeId as string, 10) === Constants.CouponPurposeId.StoreAds) {
        couponString = `${couponString} ${i18n.getString(_TL_('This offer only applies to the amount you spend on your Microsoft Store Ads campaigns.'))}`;
      }

      return couponString;
    });

  const token = createAsyncToken(promise);
  token.on({
    onSuccess: (info) => {
      setLoading(false);
      setCouponInfo(info);
    },
    onError: ({ message }) => {
      setLoading(false);
      setMessages([message]);
    },
  });
  return token.cancel;
};

export const CouponInput = ({
  ccBaseUrl,
  cid,
  countryCode,
  currencyId,
  i18n,
  initialCouponCode,
  onValidate,
  setLoading,
  restfulService,
}: CouponInputProps) => {
  const [couponCode, setCouponCode] = useState(initialCouponCode || '');
  const [couponInfo, setCouponInfo] = useState<string | null>(null);
  const [isValid, setIsValid] = useState(false);
  const { validationErrors, setValidationErrors } = useContext(ValidationErrorContext);
  const errors = useMemo(
    () => _.chain(validationErrors)
      .where({ FieldId: CouponCodeFieldId })
      .pluck('Message')
      .value() as string[],
    [validationErrors]
  );
  const [messages, setMessages] = useState(errors);

  const combinedMessages = useMemo(() => _.union(errors, messages), [errors, messages]);

  const clearValidationErrors = useCallback(
    () => {
      // remove the coupon errors from validation errors
      const updatedValidationErrors = _.reject(
        validationErrors,
        ({
          FieldId,
        }: ValidationError) => FieldId === CouponCodeFieldId
      );
      // if lengths are the same, the object should be the same
      if (validationErrors.length !== updatedValidationErrors.length) {
        setValidationErrors(updatedValidationErrors);
      }
    },
    [validationErrors, setValidationErrors]
  );

  const i18nLengthMessage = useMemo(
    () => i18n.getString(_TL_('Promotional offer must be 16 characters long')),
    [i18n]
  );

  const couponBaseUrlBuilder = useMemo(
    () => {
      const urlBuilders = getUrlBuilders({
        ccBaseUrl,
        cidFetcher: _.constant(cid),
        getDefaultIsShortUrl: _.constant(false),
      });
      return new urlBuilders.BaseUrlBuilder('coupon')
        .action('validatecoupon')
        .params({
          currencyCode: currencyId,
          countryCode,
          couponType: 0, // unspecified couponType
        }) as CouponUrlBuilder;
    },
    [ccBaseUrl, cid, currencyId, countryCode]
  );

  useEffect(
    // startCouponValidation returns the token cancellation function, so it is called if the coupon code updates again
    () => (couponCode.length === CouponLength
      ? startCouponValidation(i18n, couponCode, setCouponInfo, setMessages, setLoading, restfulService, couponBaseUrlBuilder)
      : _.noop),
    [
      couponBaseUrlBuilder,
      couponCode,
      i18n,
      restfulService,
      setLoading,
    ]
  );

  useLayoutEffect(
    () => setIsValid((couponCode.length === 0 || couponInfo) as boolean),
    [couponCode, couponInfo]
  );

  useLayoutEffect(
    () => onValidate({
      couponCode,
      isValid,
    }),
    [couponCode, isValid, onValidate]
  );

  return (
    <Fragment>
      <LineByLineFormGroup
        label={i18n.getString(_TL_('Promotional offer (optional)'))}
        helpPopupTopic="Page_Account_Details_PromoCode"
        helpPopupAriaLabel={i18n.getString(_TL_('Promotional offer tooltip'))}
        flyoutMessageHtml={couponInfo}
      >
        <Input
          className="form-control"
          name="coupon input"
          placeholder={i18n.getString(_TL_('Enter a valid promotional offer code'))}
          value={couponCode}
          onChange={useCallback(({
            target: { value },
          }: React.ChangeEvent<HTMLInputElement>) => {
            clearValidationErrors();
            if (messages.length) {
              setMessages([]);
            }
            setCouponInfo(null);
            setCouponCode(value);
          }, [clearValidationErrors, messages])}
          maxLength={CouponLength}
          onBlur={useCallback(
            () => {
              if (couponCode.length !== 0 && couponCode.length !== CouponLength) {
                setMessages([i18nLengthMessage]);
                clearValidationErrors();
              }
            },
            [couponCode, clearValidationErrors, i18nLengthMessage]
          )}
        />
        <ErrorMessages messages={combinedMessages} />
      </LineByLineFormGroup>
    </Fragment>
  );
};

CouponInput.propTypes = {
  ccBaseUrl: PropTypes.string.isRequired,
  cid: PropTypes.number.isRequired,
  countryCode: PropTypes.string.isRequired,
  currencyId: PropTypes.number.isRequired,
  i18n: i18nPropType.isRequired,
  initialCouponCode: PropTypes.string,
  onValidate: PropTypes.func.isRequired,
  setLoading: PropTypes.func.isRequired,
  restfulService: PropTypes.shape({
    get: PropTypes.func.isRequired,
  }).isRequired,
} as ValidationMap<CouponInputProps>;

CouponInput.defaultProps = {
  initialCouponCode: '',
};

export type {
  CouponInputI18n,
  CouponInputProps,
  CouponRestfulService,
  CouponUrlBuilder,
  CouponValidationRequest,
  CouponValidationResponse,
  CouponValidationResult,
} from './types';
